import { useNavigate } from 'react-router';
import {
  useCreateFund,
  useUpdateFund,
  useCreateBankAccount,
  useUpdateBankAccount,
  useDeactivateBankAccount,
  useCreateFundEntity,
  useUpdateFundEntity,
  useDeleteFundEntity,
} from '@/hooks/react-query/useFunds';
import {
  useCreateFundLPATerms,
  useUpdateFundLPATerms,
} from '@/hooks/react-query/useFundLPATerms';
import { useToast } from '@/hooks/useToast';
import { buildMutationErrorHandler } from '@/utils/form-errors';
import type {
  FundFormData,
  BankAccountFormData,
  FundEntityFormData,
} from './types';
import {
  transformFormToFundData,
  transformFormToLPATermsData,
} from './fundFormTransformers';
import React from 'react';

interface UseFundFormSaveOptions {
  fundId?: string;
  isEditing: boolean;
  initialBankAccounts?: BankAccountFormData[];
  initialFundEntities?: FundEntityFormData[];
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export function useFundFormSave({
  fundId,
  isEditing,
  initialBankAccounts = [],
  initialFundEntities = [],
  setErrors,
}: UseFundFormSaveOptions) {
  const navigate = useNavigate();
  const createFundMutation = useCreateFund();
  const updateFundMutation = useUpdateFund();
  const createLPATermsMutation = useCreateFundLPATerms();
  const updateLPATermsMutation = useUpdateFundLPATerms();
  const createBankAccountMutation = useCreateBankAccount();
  const updateBankAccountMutation = useUpdateBankAccount();
  const deactivateBankAccountMutation = useDeactivateBankAccount();
  const createFundEntityMutation = useCreateFundEntity();
  const updateFundEntityMutation = useUpdateFundEntity();
  const deleteFundEntityMutation = useDeleteFundEntity();
  const { toast } = useToast();

  const isSaving =
    createFundMutation.isPending ||
    updateFundMutation.isPending ||
    createLPATermsMutation.isPending ||
    updateLPATermsMutation.isPending ||
    createBankAccountMutation.isPending ||
    updateBankAccountMutation.isPending ||
    deactivateBankAccountMutation.isPending ||
    createFundEntityMutation.isPending ||
    updateFundEntityMutation.isPending ||
    deleteFundEntityMutation.isPending;

  const handleMutationError = (
    error: Error,
    context: 'fund' | 'lpa-terms',
    fundWasUpdated = false
  ) => {
    console.error(
      `Failed to ${context === 'fund' ? 'save fund' : 'save LPA terms'}:`,
      error
    );

    buildMutationErrorHandler(setErrors, {
      fallbackMessage: `Failed to ${context === 'fund' ? 'save fund' : 'save LPA terms'}`,
      onValidationError: () => {
        const title =
          context === 'lpa-terms' && fundWasUpdated
            ? 'LPA Terms Validation Error'
            : 'Validation Error';
        const description =
          context === 'lpa-terms' && fundWasUpdated
            ? 'Fund updated, but LPA terms have validation errors. Please fix them.'
            : 'Please fix the highlighted errors in the form.';
        toast({ title, description, variant: 'destructive' });
      },
      onFallbackError: (message) => {
        if (context !== 'lpa-terms') return;
        const description = fundWasUpdated
          ? `Fund updated, but failed to save LPA terms: ${message}`
          : message;
        toast({ description, variant: 'destructive' });
      },
    })(error);
  };

  const saveLPATermsForExistingFund = (
    targetFundId: string,
    lpaTermsData: ReturnType<typeof transformFormToLPATermsData>
  ) => {
    // Always try to update LPA terms first (safer approach)
    updateLPATermsMutation.mutate(
      { fundId: targetFundId, data: lpaTermsData },
      {
        onSuccess: () => {
          navigate(`/app/funds/${targetFundId}`);
        },
        onError: (updateError: Error) => {
          console.log('Update failed, trying create:', updateError);
          // If update fails (e.g., no existing terms), try to create
          createLPATermsMutation.mutate(
            { ...lpaTermsData, fund_id: targetFundId },
            {
              onSuccess: () => {
                navigate(`/app/funds/${targetFundId}`);
              },
              onError: (createError: Error) => {
                handleMutationError(createError, 'lpa-terms', true);
              },
            }
          );
        },
      }
    );
  };

  const saveLPATermsForNewFund = (
    newFundId: string,
    lpaTermsData: ReturnType<typeof transformFormToLPATermsData>
  ) => {
    createLPATermsMutation.mutate(
      { ...lpaTermsData, fund_id: newFundId },
      {
        onSuccess: () => {
          navigate(`/app/funds/${newFundId}`);
        },
        onError: (createError: Error) => {
          console.error(
            'Failed to create LPA terms for new fund:',
            createError
          );

          buildMutationErrorHandler(setErrors, {
            fallbackMessage: 'Failed to create LPA terms',
            onValidationError: () => {
              toast({
                title: 'LPA Terms Validation Error',
                description:
                  'Fund created, but LPA terms have validation errors. Please fix them.',
                variant: 'destructive',
              });
              // Navigate to fund but stay on edit to fix errors
              navigate(`/app/funds/${newFundId}/edit`);
            },
            onFallbackError: (message) => {
              toast({
                description: `Fund created, but failed to create LPA terms: ${message}`,
                variant: 'destructive',
              });
              navigate(`/app/funds/${newFundId}`);
            },
          })(createError);
        },
      }
    );
  };

  // Helper to check if a bank account ID is a temporary client-side ID
  const isTemporaryId = (id: string) => id.startsWith('bank-');

  const saveBankAccounts = async (
    targetFundId: string,
    bankAccounts: BankAccountFormData[]
  ) => {
    // Determine which accounts are new, updated, or deleted
    const initialIds = new Set(initialBankAccounts.map((a) => a.id));
    const currentIds = new Set(bankAccounts.map((a) => a.id));

    // Accounts to create (new ones with temporary IDs)
    const accountsToCreate = bankAccounts.filter((a) => isTemporaryId(a.id));

    // Accounts to update (existing ones that are still present)
    const accountsToUpdate = bankAccounts.filter(
      (a) => !isTemporaryId(a.id) && initialIds.has(a.id)
    );

    // Accounts to deactivate (were in initial but not in current)
    const accountsToDeactivate = initialBankAccounts.filter(
      (a) => !currentIds.has(a.id)
    );

    // Process all bank account operations
    const operations: Promise<unknown>[] = [];

    // Create new accounts
    // Note: Type assertions needed due to upstream useApiMutation generic ordering issue
    type CreateBankAccountVars = {
      fundId: string;
      data: {
        account_name: string;
        account_type: string;
        bank_name: string;
        iban: string;
        swift_bic: string;
        account_number: string;
        currency: string;
        is_primary: boolean;
      };
    };
    const createBankAccount = createBankAccountMutation.mutate as unknown as (
      variables: CreateBankAccountVars,
      options: { onSuccess: () => void; onError: (error: Error) => void }
    ) => void;

    for (const account of accountsToCreate) {
      operations.push(
        new Promise((resolve, reject) => {
          createBankAccount(
            {
              fundId: targetFundId,
              data: {
                account_name: account.account_name,
                account_type: account.account_type,
                bank_name: account.bank_name,
                iban: account.iban,
                swift_bic: account.swift_bic,
                account_number: account.account_number,
                currency: account.currency,
                is_primary: account.is_primary,
              },
            },
            {
              onSuccess: () => resolve(undefined),
              onError: reject,
            }
          );
        })
      );
    }

    // Update existing accounts
    type UpdateBankAccountVars = CreateBankAccountVars & { accountId: string };
    const updateBankAccount = updateBankAccountMutation.mutate as unknown as (
      variables: UpdateBankAccountVars,
      options: { onSuccess: () => void; onError: (error: Error) => void }
    ) => void;

    for (const account of accountsToUpdate) {
      const initialAccount = initialBankAccounts.find(
        (a) => a.id === account.id
      );
      // Only update if something changed
      if (
        initialAccount &&
        (initialAccount.account_name !== account.account_name ||
          initialAccount.account_type !== account.account_type ||
          initialAccount.bank_name !== account.bank_name ||
          initialAccount.iban !== account.iban ||
          initialAccount.swift_bic !== account.swift_bic ||
          initialAccount.account_number !== account.account_number ||
          initialAccount.currency !== account.currency ||
          initialAccount.is_primary !== account.is_primary)
      ) {
        operations.push(
          new Promise((resolve, reject) => {
            updateBankAccount(
              {
                fundId: targetFundId,
                accountId: account.id,
                data: {
                  account_name: account.account_name,
                  account_type: account.account_type,
                  bank_name: account.bank_name,
                  iban: account.iban,
                  swift_bic: account.swift_bic,
                  account_number: account.account_number,
                  currency: account.currency,
                  is_primary: account.is_primary,
                },
              },
              {
                onSuccess: () => resolve(undefined),
                onError: reject,
              }
            );
          })
        );
      }
    }

    // Deactivate removed accounts
    type DeactivateBankAccountVars = { fundId: string; accountId: string };
    const deactivateBankAccount =
      deactivateBankAccountMutation.mutate as unknown as (
        variables: DeactivateBankAccountVars,
        options: { onSuccess: () => void; onError: (error: Error) => void }
      ) => void;

    for (const account of accountsToDeactivate) {
      operations.push(
        new Promise((resolve, reject) => {
          deactivateBankAccount(
            {
              fundId: targetFundId,
              accountId: account.id,
            },
            {
              onSuccess: () => resolve(undefined),
              onError: reject,
            }
          );
        })
      );
    }

    // Wait for all operations to complete
    if (operations.length > 0) {
      try {
        await Promise.all(operations);
      } catch (error) {
        console.error('Error saving bank accounts:', error);
        toast({
          title: 'Bank Account Error',
          description:
            'Some bank account changes may not have been saved. Please review and try again.',
          variant: 'destructive',
        });
      }
    }
  };

  // Helper to check if a fund entity ID is a temporary client-side ID
  const isTemporaryEntityId = (id: string) => id.startsWith('entity-');

  const saveFundEntities = async (
    targetFundId: string,
    fundEntities: FundEntityFormData[]
  ) => {
    // Determine which entities are new, updated, or deleted
    const initialIds = new Set(initialFundEntities.map((e) => e.id));
    const currentIds = new Set(fundEntities.map((e) => e.id));

    // Entities to create (new ones with temporary IDs)
    const entitiesToCreate = fundEntities.filter((e) =>
      isTemporaryEntityId(e.id)
    );

    // Entities to update (existing ones that are still present)
    const entitiesToUpdate = fundEntities.filter(
      (e) => !isTemporaryEntityId(e.id) && initialIds.has(e.id)
    );

    // Entities to delete (were in initial but not in current)
    const entitiesToDelete = initialFundEntities.filter(
      (e) => !currentIds.has(e.id)
    );

    // Process all fund entity operations
    const operations: Promise<unknown>[] = [];

    // Create new entities
    type CreateFundEntityVars = {
      fundId: string;
      data: {
        entity_name: string;
        role: string;
        jurisdiction: string;
        registration_number: string;
        iban: string | null;
      };
    };
    const createFundEntity = createFundEntityMutation.mutate as unknown as (
      variables: CreateFundEntityVars,
      options: { onSuccess: () => void; onError: (error: Error) => void }
    ) => void;

    for (const entity of entitiesToCreate) {
      operations.push(
        new Promise((resolve, reject) => {
          createFundEntity(
            {
              fundId: targetFundId,
              data: {
                entity_name: entity.entity_name,
                role: entity.role,
                jurisdiction: entity.jurisdiction,
                registration_number: entity.registration_number,
                iban: entity.iban || null,
              },
            },
            {
              onSuccess: () => resolve(undefined),
              onError: reject,
            }
          );
        })
      );
    }

    // Update existing entities
    type UpdateFundEntityVars = CreateFundEntityVars & { entityId: string };
    const updateFundEntity = updateFundEntityMutation.mutate as unknown as (
      variables: UpdateFundEntityVars,
      options: { onSuccess: () => void; onError: (error: Error) => void }
    ) => void;

    for (const entity of entitiesToUpdate) {
      const initialEntity = initialFundEntities.find((e) => e.id === entity.id);
      // Only update if something changed
      if (
        initialEntity &&
        (initialEntity.entity_name !== entity.entity_name ||
          initialEntity.role !== entity.role ||
          initialEntity.jurisdiction !== entity.jurisdiction ||
          initialEntity.registration_number !== entity.registration_number ||
          initialEntity.iban !== entity.iban)
      ) {
        operations.push(
          new Promise((resolve, reject) => {
            updateFundEntity(
              {
                fundId: targetFundId,
                entityId: entity.id,
                data: {
                  entity_name: entity.entity_name,
                  role: entity.role,
                  jurisdiction: entity.jurisdiction,
                  registration_number: entity.registration_number,
                  iban: entity.iban || null,
                },
              },
              {
                onSuccess: () => resolve(undefined),
                onError: reject,
              }
            );
          })
        );
      }
    }

    // Delete removed entities
    type DeleteFundEntityVars = { fundId: string; entityId: string };
    const deleteFundEntity = deleteFundEntityMutation.mutate as unknown as (
      variables: DeleteFundEntityVars,
      options: { onSuccess: () => void; onError: (error: Error) => void }
    ) => void;

    for (const entity of entitiesToDelete) {
      operations.push(
        new Promise((resolve, reject) => {
          deleteFundEntity(
            {
              fundId: targetFundId,
              entityId: entity.id,
            },
            {
              onSuccess: () => resolve(undefined),
              onError: reject,
            }
          );
        })
      );
    }

    // Wait for all operations to complete
    if (operations.length > 0) {
      try {
        await Promise.all(operations);
      } catch (error) {
        console.error('Error saving fund entities:', error);
        toast({
          title: 'Fund Entity Error',
          description:
            'Some fund entity changes may not have been saved. Please review and try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const save = (
    formData: FundFormData,
    bankAccounts: BankAccountFormData[] = [],
    fundEntities: FundEntityFormData[] = []
  ) => {
    const fundData = transformFormToFundData(formData);
    const lpaTermsData = transformFormToLPATermsData(formData);

    if (isEditing && fundId) {
      // Update existing fund
      // Note: Type assertion needed due to upstream useApiMutation generic ordering issue
      (
        updateFundMutation.mutate as unknown as (
          variables: { id: string; data: typeof fundData },
          options: {
            onSuccess: () => void;
            onError: (error: Error) => void;
          }
        ) => void
      )(
        { id: fundId, data: fundData },
        {
          onSuccess: () => {
            // Save bank accounts and fund entities in parallel with LPA terms
            saveBankAccounts(fundId, bankAccounts);
            saveFundEntities(fundId, fundEntities);
            saveLPATermsForExistingFund(fundId, lpaTermsData);
          },
          onError: (error: Error) => {
            handleMutationError(error, 'fund');
          },
        }
      );
    } else {
      // Create new fund - need to add commitment_amount for FundCreate
      const createData = { ...fundData, commitment_amount: 0 };
      // Note: Type assertion needed due to upstream useApiMutation generic ordering issue
      (
        createFundMutation.mutate as unknown as (
          variables: typeof createData,
          options: {
            onSuccess: (newFund: { id: string }) => void;
            onError: (error: Error) => void;
          }
        ) => void
      )(createData, {
        onSuccess: (newFund: { id: string }) => {
          saveLPATermsForNewFund(newFund.id, lpaTermsData);
        },
        onError: (error: Error) => {
          handleMutationError(error, 'fund');
        },
      });
    }
  };

  return { save, isSaving };
}
